import { ReactNode } from "react";
import styled from "styled-components";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";

import { Container } from "./styledComponents";

// Clears the persisted Recoil state (recoil-persist) so a stale/corrupt
// rehydrated shape can't keep crashing the editor on every open. Matches the
// keys used in MVVM/State/persist.ts ("allstar-global-state" and the staging
// "dev-" prefixed variant).
const clearPersistedState = () => {
  try {
    Object.keys(window.localStorage)
      .filter((key) => key.includes("allstar-global-state"))
      .forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // localStorage may be unavailable (private mode / blocked) — reloading
    // is still worth attempting.
  }
};

const Fallback = ({ resetErrorBoundary }: FallbackProps) => (
  <Container
    alignItems={"center"}
    justifyContent={"center"}
    minHeight={400}
    flexDirection={"column"}
  >
    <FallbackTitle>Something went wrong opening the editor</FallbackTitle>
    <FallbackText>
      This is usually caused by an outdated saved setting. Resetting your saved
      options will fix it.
    </FallbackText>
    <FallbackButton
      onClick={() => {
        clearPersistedState();
        resetErrorBoundary();
        window.location.reload();
      }}
    >
      Reset and reload
    </FallbackButton>
  </Container>
);

export const QuickEditErrorBoundary = ({
  children,
}: {
  children: ReactNode;
}) => <ErrorBoundary FallbackComponent={Fallback}>{children}</ErrorBoundary>;

const FallbackTitle = styled.div`
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow, sans-serif;
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 8px;
  text-align: center;
`;

const FallbackText = styled.div`
  color: ${({ theme }) => theme.colors.chalk};
  font-family: Barlow, sans-serif;
  font-size: 14px;
  margin-bottom: 20px;
  max-width: 360px;
  text-align: center;
`;

const FallbackButton = styled.button`
  background: ${({ theme }) => theme.colors.envy};
  border: none;
  border-radius: 22px;
  color: ${({ theme }) => theme.colors.darkerNed};
  cursor: pointer;
  font-family: Barlow, sans-serif;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 24px;
  transition: opacity 150ms ease;

  &:hover {
    opacity: 0.9;
  }
`;
